import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Box, Button, Stack, Typography } from '@mui/material';
import { cyan, indigo, purple } from '@mui/material/colors';
import { format as formatDate, isAfter, isBefore, subMinutes } from 'date-fns';
import { compact, concat, find, groupBy, map, maxBy } from 'lodash';
import { useMemo } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { Link } from 'react-router-dom';

import { Detection, useDetections } from '@/api/historicalData';
import { useSplBreachDetections } from '@/api/historicalData/useSplBreachDetections';
import { ErrorIcon } from '@/components/StatusIcons';
import { Datum } from '@/components/data/Datum';
import { MapMarker } from '@/components/map/MapMarker';
import { EnrichedMapDevice } from '@/components/map/SpotterMap';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useDateRangeForSample } from '@/hooks/useDateRangeForSample';
import { useNow } from '@/hooks/useNow';
import { useTzContext } from '@/hooks/useTzContext';
import { lngLat, prettyOperator } from '@/utils';

export const MapDetectionChips = ({ devices }: { devices: EnrichedMapDevice[] }) => (
    <>
        {devices.map((device) => (
            <DeviceDetectionChips key={device.spotterId} device={device} />
        ))}
    </>
);

const DETECTION_TYPES = [
    { classification: 'boats', label: 'Vessel', color: cyan[800], bgcolor: '#F0FBFE', Icon: ErrorIcon },
    { classification: 'vessels', label: 'Vessel', color: cyan[800], bgcolor: '#F0FBFE', Icon: ErrorIcon },
    { classification: 'dolphins', label: 'Dolphin', color: indigo[500], bgcolor: '#F6F6FD', Icon: ErrorIcon },
    { classification: 'splBreach', label: 'SPL', color: purple[700], bgcolor: '#F9F2FA', Icon: VolumeUpIcon },
] as const;

const DeviceDetectionChips = ({ device }: { device: EnrichedMapDevice }) => {
    const mixpanel = useMixpanel();
    const { selectedTimestamp } = useTimelineNavigationContext();
    const { isTrackingLive } = useDateRangeContext();
    const tz = useTzContext();
    const now = useNow(300, { enabled: !selectedTimestamp });
    const targetTime = selectedTimestamp ?? now;
    // TODO: grace period should depend on expected reporting cadence of whatever data is driving this detection/alert type
    // https://app.shortcut.com/sofar/story/284300
    const targetEndWithGrace = isTrackingLive ? subMinutes(targetTime, 5) : targetTime;
    const dateRange = useDateRangeForSample(targetTime);
    const { data: allDetections } = useDetections({ dateRange, spotterId: device.spotterId });

    const { data: allSplBreaches } = useSplBreachDetections({
        spotterId: device.spotterId,
        dateRange,
    });

    const detections = useMemo(() => {
        const detectionsByClass = groupBy(concat(allDetections ?? [], allSplBreaches ?? []), 'classification');
        return compact(
            map(detectionsByClass, (detections) =>
                maxBy(
                    detections.filter(
                        (d) =>
                            d.is_detection &&
                            !isBefore(targetTime, d.start_time) &&
                            !isAfter(targetEndWithGrace, d.end_time)
                    ),
                    'end_time'
                )
            )
        );
    }, [allDetections, allSplBreaches, targetEndWithGrace, targetTime]);

    const position = useMemo(() => lngLat(device.location ?? device.track?.[0] ?? {}), [device.location, device.track]);

    if (!detections.length) {
        return false;
    }

    return (
        <MapMarker position={position} anchor="top" offset={[0, 20]}>
            <Stack sx={{ gap: 0.5 }}>
                {/* iterating over DETECTION_TYPES instead of detections for stable order */}
                {DETECTION_TYPES.map(({ classification, Icon, bgcolor, color, label }) => {
                    const detection = find(detections, { classification });
                    if (!detection) return null;
                    return (
                        <Button
                            component={Link}
                            to={`/spotter/${device.spotterId}?startDate=${formatDate(detection.start_time, 'yyyy-MM-dd', tz)}&endDate=${formatDate(detection.end_time, 'yyyy-MM-dd', tz)}#observation-data`}
                            state={{ selectedTimestamp: detection.start_time, detectionClass: classification }}
                            onClick={() =>
                                mixpanel?.track('Click detection chip', {
                                    spotterId: device.spotterId,
                                    detectionClass: classification,
                                    isTrackingLive,
                                })
                            }
                            key={detection.classification}
                            variant="contained"
                            sx={{
                                boxShadow: 1,
                                py: 0.25,
                                px: 0.5,
                                color,
                                border: '1px solid',
                                borderColor: color,
                                borderRadius: 1,
                                backgroundColor: bgcolor,
                                '&:hover': { boxShadow: 1 },
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    gap: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: 1,
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Icon color="inherit" sx={{ fontSize: '18px' }} />{' '}
                                <MapDetectionChipLabel label={label} detection={detection} />{' '}
                                <ArrowForwardIcon sx={{ fontSize: '18px' }} />
                            </Typography>
                        </Button>
                    );
                })}
            </Stack>
        </MapMarker>
    );
};

const MapDetectionChipLabel = ({ label, detection }: { label: string; detection: Detection }) => {
    if (!detection.breachedSensorAlert) {
        return <Box sx={{ mt: '1px' }}>{label} detected</Box>;
    }
    const { logicalOperator, convertedAlertUnits, convertedAlertValue, unitType } = detection.breachedSensorAlert;
    return (
        <Box sx={{ mt: '1px' }}>
            {label} {prettyOperator(logicalOperator)}{' '}
            <Datum
                value={convertedAlertValue}
                sourceUnit={convertedAlertUnits}
                type={unitType}
                maxDecimalPlaces={1}
                variant="inherit"
            />
        </Box>
    );
};
